import React, { forwardRef, ReactNode, useCallback, useRef } from 'react';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  id?: string;
  children: ReactNode;
  active?: boolean;
  disabled?: boolean;
  tooltip?: string;
  elementRef?: (el: HTMLButtonElement | null) => void;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      id,
      children,
      onClick,
      active = false,
      disabled = false,
      className = '',
      tooltip,
      elementRef,
      ...props
    },
    ref
  ) => {
    const innerRef = useRef<HTMLButtonElement | null>(null);

    // Stable ref callback
    const setRefs = useCallback(
      (node: HTMLButtonElement | null) => {
        innerRef.current = node;
        
        // Handle elementRef prop
        if (elementRef) {
          elementRef(node);
        }
        
        // Handle forwarded ref
        if (ref) {
          if (typeof ref === 'function') {
            ref(node);
          } else {
            (ref as React.MutableRefObject<HTMLButtonElement | null>).current = node;
          }
        }
      },
      [ref, elementRef]
    );
    
    return (
      <button
        {...props}
        id={id}
        ref={setRefs}
        onClick={onClick}
        disabled={disabled}
        className={`flex h-[32px] w-auto min-w-[32px] items-center justify-center rounded-md p-[5px] transition-all border border-transparent ${
          active
            ? 'bg-accent/10 text-accent ring-1 ring-accent border-accent/20 shadow-[0_0_10px_rgba(59,130,246,0.3)]'
            : 'hover:bg-interactive-hover hover:ring-1 hover:ring-border-strong'
        } ${disabled ? 'cursor-not-allowed opacity-50 hover:bg-transparent hover:ring-0' : 'cursor-pointer'} ${className}`}
        title={tooltip}
      >
        {children}
      </button>
    );
  }
);

Button.displayName = 'Button';
